/**
 * Wires the org-chart's inline quick-action menus to the structural CRUD
 * API. Owns the modal state + mutations for renaming / re-heading / moving /
 * deleting units and teams, and moving a person to another team, then hands
 * the chart:
 *   - `unitActions(unit)`  → ⋮ menu items for a function/department box
 *   - `teamActions(team)`  → ⋮ menu items for a team box
 *   - `teamCandidates` + `onSetTeam` → the person popover's "Change team"
 *   - `modals`             → the dialogs to render once, at the page root
 *
 * Delete is gated the same way the API enforces it (409 unless empty): the
 * menu item is disabled with a hint that names exactly what's blocking it, so
 * the user never fires a request that can only fail.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OrgUnitType } from '@lms/shared-types'
import { useToast } from '../../../primitives/Toast'
import type { RowAction } from '../../../primitives/RowActionsMenu'
import { ApiError } from '../../../../api/client'
import { updateOrgUnit, deleteOrgUnit, createOrgUnit } from '../../../../api/org-units'
import { renameTeam, deleteTeam, createTeam } from '../../../../api/admin-teams'
import { setEmployeeTeam } from '../../../../api/employees'
import { UNASSIGNED_TEAM_ID } from '../../../../lib/sentinels'
import type { OrgTreeTeamNode, OrgTreeUnitNode } from '../../../../lib/org-chart'
import type { ApproverCandidate } from './OrgTreeView'
import {
  RenameModal,
  UnitHeadModal,
  UnitParentModal,
  UnitTypeModal,
  AddChildModal,
  AddMembersModal,
  type ChildKind,
} from './OrgQuickEditModals'
import { TeamUnitModal, type EmpLite, type UnitLite } from './OrgStructureEditors'

/** What each box may contain, per the org hierarchy:
 *  company → function/department/team, function → department/team,
 *  department → team. Teams hold members, handled separately. */
function allowedChildKinds(unitType: OrgUnitType): ChildKind[] {
  if (unitType === 'company') return ['function', 'department', 'team']
  if (unitType === 'function') return ['department', 'team']
  return ['team']
}

/** The unit itself plus every descendant id — the set that can't be a valid
 *  new parent (moving under any of them would form a cycle; the API rejects
 *  it, but we filter the picker so the user never sees an invalid choice). */
function selfAndDescendantIds(unit: OrgTreeUnitNode): Set<string> {
  const ids = new Set<string>()
  const stack: OrgTreeUnitNode[] = [unit]
  while (stack.length > 0) {
    const cur = stack.pop()
    if (cur === undefined) continue
    ids.add(cur.id)
    stack.push(...cur.childUnits)
  }
  return ids
}

function apiErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.message) return err.message
  if (err instanceof Error && err.message) return err.message
  return fallback
}

function plural(n: number, one: string): string {
  return `${n} ${one}${n === 1 ? '' : 's'}`
}

type ActiveModal =
  | { kind: 'unit-rename'; unit: OrgTreeUnitNode }
  | { kind: 'unit-head'; unit: OrgTreeUnitNode }
  | { kind: 'unit-move'; unit: OrgTreeUnitNode }
  | { kind: 'unit-type'; unit: OrgTreeUnitNode }
  | { kind: 'unit-add'; unit: OrgTreeUnitNode }
  | { kind: 'team-rename'; team: OrgTreeTeamNode }
  | { kind: 'team-move'; team: OrgTreeTeamNode }
  | { kind: 'team-add-members'; team: OrgTreeTeamNode }
  | null

export interface OrgChartQuickActions {
  unitActions: (unit: OrgTreeUnitNode) => RowAction[]
  teamActions: (team: OrgTreeTeamNode) => RowAction[]
  teamCandidates: ApproverCandidate[]
  onSetTeam: (employeeId: string, teamId: string) => Promise<void>
  /** Move a person to the 'Unassigned' holder team (there is no team-less
   *  state). */
  onRemoveFromTeam: (employeeId: string) => Promise<void>
  /** The 'Unassigned' holder team id — the popover hides "Remove from team"
   *  for someone already there. */
  unassignedTeamId: string
  modals: JSX.Element
}

export function useOrgChartQuickActions({
  units,
  employees,
  teams,
}: {
  /** All units, for the "Move team to unit" picker. */
  units: UnitLite[]
  /** All employees, for the unit-head picker. */
  employees: EmpLite[]
  /** All teams (id + name), for the person popover's "Change team". */
  teams: { id: string; name: string }[]
}): OrgChartQuickActions {
  const queryClient = useQueryClient()
  const { notify } = useToast()
  const [active, setActive] = useState<ActiveModal>(null)

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['hr', 'org-chart'] }),
      queryClient.invalidateQueries({ queryKey: ['teams'] }),
    ])
  }

  // ── Non-modal mutations (menu items that act directly) ────────────────────

  const removeHead = useMutation({
    mutationFn: (unit: OrgTreeUnitNode) =>
      updateOrgUnit(unit.id, { headEmployeeId: null }),
    onSuccess: async () => {
      await invalidate()
      notify('Head removed', 'success')
    },
    onError: (err) =>
      notify(`Couldn't remove head — ${apiErrorMessage(err, 'failed')}`, 'error'),
  })

  const removeUnit = useMutation({
    mutationFn: (unit: OrgTreeUnitNode) => deleteOrgUnit(unit.id),
    onSuccess: async () => {
      await invalidate()
      notify('Unit deleted', 'success')
    },
    onError: (err) =>
      notify(`Couldn't delete — ${apiErrorMessage(err, 'failed')}`, 'error'),
  })

  const removeTeam = useMutation({
    mutationFn: (team: OrgTreeTeamNode) => deleteTeam(team.id),
    onSuccess: async () => {
      await invalidate()
      notify('Team deleted', 'success')
    },
    onError: (err) =>
      notify(`Couldn't delete — ${apiErrorMessage(err, 'failed')}`, 'error'),
  })

  const setTeamMutation = useMutation({
    mutationFn: ({ employeeId, teamId }: { employeeId: string; teamId: string }) =>
      setEmployeeTeam(employeeId, teamId),
    onSuccess: async () => {
      await invalidate()
      notify('Team updated', 'success')
    },
  })

  // The popover awaits this and surfaces its own inline error on reject, so
  // rethrow rather than swallowing (no error toast here — the popover shows it).
  const onSetTeam = useMemo(
    () => async (employeeId: string, teamId: string) => {
      await setTeamMutation.mutateAsync({ employeeId, teamId })
    },
    [setTeamMutation],
  )

  // "Remove from team" is just a move to the Unassigned holder team.
  const onRemoveFromTeam = useMemo(
    () => async (employeeId: string) => {
      await setTeamMutation.mutateAsync({
        employeeId,
        teamId: UNASSIGNED_TEAM_ID,
      })
    },
    [setTeamMutation],
  )

  // ── Menu builders ─────────────────────────────────────────────────────────

  const unitActions = (unit: OrgTreeUnitNode): RowAction[] => {
    const childN = unit.childUnits.length
    const teamN = unit.teams.length
    const parts: string[] = []
    if (childN > 0) parts.push(plural(childN, 'sub-unit'))
    if (teamN > 0) parts.push(plural(teamN, 'team'))
    const blocked = parts.length > 0
    const deleting = removeUnit.isPending && removeUnit.variables?.id === unit.id

    const actions: RowAction[] = [
      {
        icon: 'add',
        label: 'Add…',
        testId: `org-tree-unit-add-${unit.id}`,
        onClick: () => setActive({ kind: 'unit-add', unit }),
      },
      {
        icon: 'edit',
        label: 'Rename',
        testId: `org-tree-unit-rename-${unit.id}`,
        onClick: () => setActive({ kind: 'unit-rename', unit }),
      },
      {
        icon: 'drive_file_move',
        label: 'Move to unit…',
        testId: `org-tree-unit-move-${unit.id}`,
        onClick: () => setActive({ kind: 'unit-move', unit }),
      },
      {
        icon: 'category',
        label: 'Change type…',
        testId: `org-tree-unit-type-${unit.id}`,
        onClick: () => setActive({ kind: 'unit-type', unit }),
      },
      {
        icon: 'manage_accounts',
        label: unit.head ? 'Change head' : 'Set head',
        testId: `org-tree-unit-head-${unit.id}`,
        onClick: () => setActive({ kind: 'unit-head', unit }),
      },
    ]
    if (unit.head) {
      actions.push({
        icon: 'person_remove',
        label: 'Remove head',
        testId: `org-tree-unit-removehead-${unit.id}`,
        onClick: () => removeHead.mutate(unit),
      })
    }
    actions.push({
      icon: 'delete',
      label: 'Delete',
      testId: `org-tree-unit-delete-${unit.id}`,
      destructive: true,
      disabled: blocked || deleting,
      ...(blocked ? { hint: `Move its ${parts.join(' and ')} out first` } : {}),
      onClick: () => {
        // eslint-disable-next-line no-alert
        if (window.confirm(`Delete the “${unit.name}” unit?`)) removeUnit.mutate(unit)
      },
    })
    return actions
  }

  const teamActions = (team: OrgTreeTeamNode): RowAction[] => {
    const memberN = team.members.length
    const isHolder = team.id === UNASSIGNED_TEAM_ID
    const blocked = memberN > 0 || isHolder
    const deleting = removeTeam.isPending && removeTeam.variables?.id === team.id
    const deleteHint = isHolder
      ? "The Unassigned team can't be deleted"
      : memberN > 0
        ? `Move its ${plural(memberN, 'member')} out first`
        : undefined
    return [
      {
        icon: 'person_add',
        label: 'Add members…',
        testId: `org-tree-team-addmembers-${team.id}`,
        onClick: () => setActive({ kind: 'team-add-members', team }),
      },
      {
        icon: 'edit',
        label: 'Rename',
        testId: `org-tree-team-rename-${team.id}`,
        onClick: () => setActive({ kind: 'team-rename', team }),
      },
      {
        icon: 'drive_file_move',
        label: 'Move to unit…',
        testId: `org-tree-team-move-${team.id}`,
        onClick: () => setActive({ kind: 'team-move', team }),
      },
      {
        icon: 'delete',
        label: 'Delete',
        testId: `org-tree-team-delete-${team.id}`,
        destructive: true,
        disabled: blocked || deleting,
        ...(deleteHint ? { hint: deleteHint } : {}),
        onClick: () => {
          // eslint-disable-next-line no-alert
          if (window.confirm(`Delete the “${team.name}” team?`)) removeTeam.mutate(team)
        },
      },
    ]
  }

  const teamCandidates = useMemo<ApproverCandidate[]>(
    () =>
      [...teams]
        .map((t) => ({ id: t.id, name: t.name, jobTitle: '' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [teams],
  )

  // ── Modals rendered once at the page root ─────────────────────────────────

  const modals = (
    <>
      <RenameModal
        open={active?.kind === 'unit-rename'}
        onClose={() => setActive(null)}
        title="Rename unit"
        label="Unit name"
        currentName={active?.kind === 'unit-rename' ? active.unit.name : ''}
        onSave={async (name) => {
          if (active?.kind !== 'unit-rename') return
          await updateOrgUnit(active.unit.id, { name })
          await invalidate()
          notify('Unit renamed', 'success')
        }}
      />
      <UnitHeadModal
        open={active?.kind === 'unit-head'}
        onClose={() => setActive(null)}
        unitName={active?.kind === 'unit-head' ? active.unit.name : ''}
        currentHeadId={
          active?.kind === 'unit-head' ? (active.unit.head?.id ?? null) : null
        }
        employees={employees}
        onSave={async (headId) => {
          if (active?.kind !== 'unit-head') return
          await updateOrgUnit(active.unit.id, { headEmployeeId: headId })
          await invalidate()
          notify(headId ? 'Head updated' : 'Head removed', 'success')
        }}
      />
      <UnitParentModal
        open={active?.kind === 'unit-move'}
        onClose={() => setActive(null)}
        unitName={active?.kind === 'unit-move' ? active.unit.name : ''}
        currentParentId={active?.kind === 'unit-move' ? active.unit.parentId : null}
        units={
          active?.kind === 'unit-move'
            ? (() => {
                const excluded = selfAndDescendantIds(active.unit)
                return units.filter((u) => !excluded.has(u.id))
              })()
            : []
        }
        onSave={async (parentId) => {
          if (active?.kind !== 'unit-move') return
          await updateOrgUnit(active.unit.id, { parentId })
          await invalidate()
          notify('Unit moved', 'success')
        }}
      />
      <UnitTypeModal
        open={active?.kind === 'unit-type'}
        onClose={() => setActive(null)}
        unitName={active?.kind === 'unit-type' ? active.unit.name : ''}
        currentType={active?.kind === 'unit-type' ? active.unit.unitType : 'department'}
        onSave={async (unitType) => {
          if (active?.kind !== 'unit-type') return
          await updateOrgUnit(active.unit.id, { unitType })
          await invalidate()
          notify('Type changed', 'success')
        }}
      />
      <RenameModal
        open={active?.kind === 'team-rename'}
        onClose={() => setActive(null)}
        title="Rename team"
        label="Team name"
        currentName={active?.kind === 'team-rename' ? active.team.name : ''}
        onSave={async (name) => {
          if (active?.kind !== 'team-rename') return
          await renameTeam(active.team.id, name)
          await invalidate()
          notify('Team renamed', 'success')
        }}
      />
      <TeamUnitModal
        open={active?.kind === 'team-move'}
        onClose={() => setActive(null)}
        units={units}
        team={
          active?.kind === 'team-move'
            ? {
                id: active.team.id,
                name: active.team.name,
                parentUnitId: active.team.parentUnitId,
              }
            : null
        }
      />
      <AddChildModal
        open={active?.kind === 'unit-add'}
        onClose={() => setActive(null)}
        parentName={active?.kind === 'unit-add' ? active.unit.name : ''}
        allowedKinds={
          active?.kind === 'unit-add'
            ? allowedChildKinds(active.unit.unitType)
            : []
        }
        employees={employees}
        onCreateUnit={async (childKind, name, headId) => {
          if (active?.kind !== 'unit-add') return
          await createOrgUnit({
            parentId: active.unit.id,
            unitType: childKind,
            name,
            headEmployeeId: headId,
          })
          await invalidate()
          notify(`Added ${childKind} “${name}”`, 'success')
        }}
        onCreateTeam={async (name) => {
          if (active?.kind !== 'unit-add') return
          await createTeam(name, active.unit.id)
          await invalidate()
          notify(`Added team “${name}”`, 'success')
        }}
      />
      <AddMembersModal
        open={active?.kind === 'team-add-members'}
        onClose={() => setActive(null)}
        teamName={active?.kind === 'team-add-members' ? active.team.name : ''}
        candidates={
          active?.kind === 'team-add-members'
            ? (() => {
                const inTeam = new Set(active.team.members.map((m) => m.id))
                return employees.filter((e) => !inTeam.has(e.id))
              })()
            : []
        }
        onAdd={async (employeeIds) => {
          if (active?.kind !== 'team-add-members') return
          const teamId = active.team.id
          await Promise.all(employeeIds.map((id) => setEmployeeTeam(id, teamId)))
          await invalidate()
          notify(
            `Added ${employeeIds.length} ${employeeIds.length === 1 ? 'member' : 'members'}`,
            'success',
          )
        }}
      />
    </>
  )

  return {
    unitActions,
    teamActions,
    teamCandidates,
    onSetTeam,
    onRemoveFromTeam,
    unassignedTeamId: UNASSIGNED_TEAM_ID,
    modals,
  }
}
